// BarcodeLabelModal.jsx
// npm i react-barcode qrcode.react
import { useEffect, useRef, useState } from "react";
import Barcode from "react-barcode";
import { QRCodeSVG } from "qrcode.react";

const LABEL_SIZES = {
  thermal: {
    label: "Thermal label — 50 × 25 mm",
    page: "50mm 25mm",
    margin: "0",
    w: "50mm",
    h: "25mm",
  },
  a4: {
    label: "A4 sheet — 3 × 8 labels",
    page: "A4",
    margin: "8mm",
    w: "63mm",
    h: "33mm",
  },
};

// Same CSS preview aur print dono mein, taaki jo dikhe wahi print ho
const labelCss = (s) => `
.bc-label{width:${s.w};height:${s.h};padding:1.5mm;box-sizing:border-box;overflow:hidden;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:0.5mm;text-align:center;background:#fff;color:#000;font-family:Arial,Helvetica,sans-serif;}
.bc-name{font-size:7.5pt;font-weight:700;line-height:1.15;max-width:100%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.bc-codes{display:flex;align-items:center;justify-content:center;gap:1.5mm;max-width:100%;min-height:0;flex:1 1 auto;}
.bc-codes svg{max-width:100%;max-height:100%;height:auto;}
.bc-sku{font-size:6.5pt;font-family:monospace;line-height:1.1;}
.bc-price{font-size:7.5pt;font-weight:600;line-height:1.1;}
`;

const escapeHtml = (str = "") =>
  String(str).replace(/[&<>"']/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  })[c]);

/**
 * Inventory item ka barcode (CODE128) / QR label preview + print.
 * Dono codes mein value = SKU, taaki ProductSKUSearch mein scan karte hi item mil jaaye.
 *
 * Props:
 *  item    -> inventory row (null = modal band)
 *  onClose -> modal band karne ke liye
 */
export default function BarcodeLabelModal({ item, onClose }) {
  const [mode, setMode] = useState("barcode"); // barcode | qr | both
  const [size, setSize] = useState("thermal");
  const [copies, setCopies] = useState(1);
  const [showPrice, setShowPrice] = useState(true);
  const [message, setMessage] = useState("");
  const labelRef = useRef(null);

  // Esc se band
  useEffect(() => {
    if (!item) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [item, onClose]);

  useEffect(() => setMessage(""), [item]);

  if (!item) return null;

  // Inventory list ke field names ke hisaab se adjust kar lena
  const sku = String(item.sku ?? item.variant?.sku ?? "").trim();
  const name = [
    item.productName ?? item.product?.title ?? item.title,
    item.variantName ?? item.variant?.name,
  ]
    .filter(Boolean)
    .join(" — ");
  const price = item.price ?? item.mrp ?? item.variant?.price;
  const s = LABEL_SIZES[size];

  const handlePrint = () => {
    const node = labelRef.current;
    if (!node || !sku) return;

    const count = Math.min(Math.max(parseInt(copies, 10) || 1, 1), 500);
    const labels = Array.from({ length: count }, () => node.outerHTML).join("");
    const isThermal = size === "thermal";

    const win = window.open("", "_blank", "width=900,height=700");
    if (!win) {
      setMessage("Popup blocked. Allow popups for this site to print labels.");
      return;
    }

    win.document.write(`<!doctype html>
<html><head><meta charset="utf-8"><title>Labels - ${escapeHtml(sku)}</title>
<style>
@page{size:${s.page};margin:${s.margin};}
html,body{margin:0;padding:0;}
body{${isThermal ? "" : "display:flex;flex-wrap:wrap;gap:2mm;align-content:flex-start;"}}
${labelCss(s)}
.bc-label{break-inside:avoid;page-break-inside:avoid;${
      isThermal
        ? "break-after:page;page-break-after:always;"
        : "outline:0.2mm dashed #bbb;"
    }}
.bc-label:last-child{break-after:auto;page-break-after:auto;}
</style></head><body>${labels}</body></html>`);
    win.document.close();
    win.focus();
    // SVG render hone ka thoda time
    setTimeout(() => {
      win.print();
      win.close();
    }, 300);
  };

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      role="dialog"
      style={{ background: "rgba(0,0,0,.5)" }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <style>{labelCss(s)}</style>

          <div className="modal-header">
            <h5 className="modal-title">Barcode / QR Label</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            <div className="mb-3">
              <div className="fw-semibold">{name || "—"}</div>
              <div className="fs--1">
                SKU: <code>{sku || "—"}</code>
              </div>
            </div>

            {!sku ? (
              <div className="alert alert-warning mb-0">
                This item has no SKU, so a barcode / QR cannot be generated.
              </div>
            ) : (
              <>
                <div className="btn-group w-100 mb-3" role="group">
                  {[
                    ["barcode", "Barcode", "ti-barcode"],
                    ["qr", "QR Code", "ti-qrcode"],
                    ["both", "Both", "ti-layout-columns"],
                  ].map(([val, label, icon]) => (
                    <button
                      key={val}
                      type="button"
                      className={`btn btn-sm ${mode === val ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setMode(val)}
                    >
                      <i className={`ti ${icon} me-1`} />
                      {label}
                    </button>
                  ))}
                </div>

                {/* Preview (zoom sirf screen ke liye, print actual size mein hoga) */}
                <div className="d-flex justify-content-center bg-light border rounded p-3 mb-3">
                  <div style={{ zoom: 1.6 }}>
                    <div className="bc-label" ref={labelRef}>
                      {name && <div className="bc-name">{name}</div>}
                      <div className="bc-codes">
                        {mode !== "qr" && (
                          <Barcode
                            value={sku}
                            format="CODE128"
                            renderer="svg"
                            width={1.3}
                            height={mode === "both" ? 34 : 40}
                            fontSize={10}
                            margin={0}
                            background="#ffffff"
                            lineColor="#000000"
                          />
                        )}
                        {mode !== "barcode" && (
                          <QRCodeSVG
                            value={sku}
                            size={mode === "both" ? 52 : 64}
                            level="M"
                          />
                        )}
                      </div>
                      {mode === "qr" && <div className="bc-sku">{sku}</div>}
                      {showPrice && price != null && price !== "" && (
                        <div className="bc-price">₹{Number(price).toFixed(2)}</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row g-2">
                  <div className="col-md-7">
                    <label className="form-label fs--1 mb-1">Label size</label>
                    <select
                      className="form-select form-select-sm"
                      value={size}
                      onChange={(e) => setSize(e.target.value)}
                    >
                      {Object.entries(LABEL_SIZES).map(([key, v]) => (
                        <option key={key} value={key}>
                          {v.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-5">
                    <label className="form-label fs--1 mb-1">Copies</label>
                    <input
                      type="number"
                      min="1"
                      max="500"
                      className="form-control form-control-sm"
                      value={copies}
                      onChange={(e) => setCopies(e.target.value)}
                    />
                  </div>
                  <div className="col-12">
                    <div className="form-check mt-1">
                      <input
                        id="bc-show-price"
                        type="checkbox"
                        className="form-check-input"
                        checked={showPrice}
                        onChange={(e) => setShowPrice(e.target.checked)}
                      />
                      <label className="form-check-label fs--1" htmlFor="bc-show-price">
                        Show price on label
                      </label>
                    </div>
                  </div>
                </div>

                {message && (
                  <div className="alert alert-warning py-2 mt-3 mb-0 fs--1">
                    {message}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-light" onClick={onClose}>
              Close
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!sku}
              onClick={handlePrint}
            >
              <i className="ti ti-printer me-1" />
              Print Labels
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}